import React, { useEffect, useMemo, useState } from 'react';
import {
  CompensationRecord,
  saveRecordAssessment,
  updateRecord,
} from '../../../../services/adminService';
import { fetchGradingTree } from '../../../../services/gradingService';
import type {
  GradingAnswer,
  GradingCalibrationAnswer,
  GradingCalibrationQuestion,
  GradingDiffFactor,
  GradingFamily,
  GradingQuestion,
  GradingRole,
  GradingSpecialization,
  GradingRoleCluster,
} from '../grading/types';
import { formatGradingScore, getCalibrationForFamily, normalizeGradingTreePayload } from '../grading/utils';
import { AdminModalTw, AdminFieldTw, btnPrimary, btnSecondary, selectClass } from '../adminUi';
import { buildRoleLabel, matchRoleByScore, questionsByFactor, visibleAnswersForQuestion } from './gradingEngine';

type AssignMode = 'quick' | 'full';
type FullStep = 'family' | 'specialization' | 'role_cluster' | 'scoring' | 'result';

type Props = {
  record: CompensationRecord;
  onClose: () => void;
  onSaved: () => void;
};

export default function RecordGradingAssignModal({ record, onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<AssignMode>('quick');

  const [families, setFamilies] = useState<GradingFamily[]>([]);
  const [roleClusters, setRoleClusters] = useState<GradingRoleCluster[]>([]);
  const [specializations, setSpecializations] = useState<GradingSpecialization[]>([]);
  const [roles, setRoles] = useState<GradingRole[]>([]);
  const [factors, setFactors] = useState<GradingDiffFactor[]>([]);
  const [questions, setQuestions] = useState<GradingQuestion[]>([]);
  const [answers, setAnswers] = useState<GradingAnswer[]>([]);
  const [calibrationQuestions, setCalibrationQuestions] = useState<GradingCalibrationQuestion[]>([]);
  const [calibrationAnswers, setCalibrationAnswers] = useState<GradingCalibrationAnswer[]>([]);

  const [quickFamilyId, setQuickFamilyId] = useState('');
  const [quickRoleClusterId, setQuickRoleClusterId] = useState('');
  const [quickSpecId, setQuickSpecId] = useState('');
  const [quickRoleId, setQuickRoleId] = useState('');

  const [fullStep, setFullStep] = useState<FullStep>('family');
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);
  const [selectedSpecId, setSelectedSpecId] = useState<number | null>(null);
  const [selectedRoleClusterId, setSelectedRoleClusterId] = useState<number | null>(null);
  const [calibAnswerId, setCalibAnswerId] = useState<number | null>(null);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<Record<number, number>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = normalizeGradingTreePayload(await fetchGradingTree());
        if (cancelled) return;
        setFamilies(data.families);
        setRoleClusters(data.roleClusters);
        setSpecializations(data.specializations);
        setRoles(data.roles);
        setFactors(data.factors);
        setQuestions(data.questions);
        setAnswers(data.answers);
        setCalibrationQuestions(data.calibrationQuestions);
        setCalibrationAnswers(data.calibrationAnswers);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Не удалось загрузить дерево грейдинга');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!roles.length || !record.role_id) return;
    const role = roles.find((r) => r.id === record.role_id);
    if (!role) return;
    const rc = roleClusters.find((s) => s.id === role.role_cluster_id);
    if (!rc) return;
    setQuickFamilyId(String(rc.family_id));
    setQuickRoleClusterId(String(role.role_cluster_id));
    setQuickSpecId(
      role.specialization_id != null ? String(role.specialization_id) : '_none',
    );
    setQuickRoleId(String(role.id));
  }, [roles, roleClusters, record.role_id]);

  const quickRoleClusterOptions = useMemo(() => {
    if (!quickFamilyId) return [];
    const familyId = Number(quickFamilyId);
    return roleClusters
      .filter((s) => s.family_id === familyId)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [roleClusters, quickFamilyId]);

  const quickSpecOptions = useMemo(() => {
    if (!quickFamilyId) return [];
    const familyId = Number(quickFamilyId);
    return specializations
      .filter((s) => s.family_id === familyId)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [specializations, quickFamilyId]);

  const quickRoleOptions = useMemo(() => {
    if (!quickRoleClusterId) return [];
    const roleClusterId = Number(quickRoleClusterId);
    return roles
      .filter((r) => {
        if (r.role_cluster_id !== roleClusterId) return false;
        if (quickSpecId === '_none') return r.specialization_id == null;
        if (quickSpecId) {
          const specId = Number(quickSpecId);
          if (r.specialization_id && r.specialization_id !== specId) return false;
        }
        return true;
      })
      .sort((a, b) => a.grade_level - b.grade_level || a.name.localeCompare(b.name, 'ru'));
  }, [roles, quickRoleClusterId, quickSpecId]);

  const quickSelectedRole = useMemo(
    () => (quickRoleId ? roles.find((r) => r.id === Number(quickRoleId)) : undefined),
    [roles, quickRoleId],
  );

  const familySpecs = useMemo(
    () => (selectedFamilyId ? specializations.filter((s) => s.family_id === selectedFamilyId) : []),
    [specializations, selectedFamilyId],
  );

  const familyRoleClusters = useMemo(
    () => (selectedFamilyId ? roleClusters.filter((s) => s.family_id === selectedFamilyId) : []),
    [roleClusters, selectedFamilyId],
  );

  const calib = useMemo(
    () => (selectedFamilyId
      ? getCalibrationForFamily(selectedFamilyId, calibrationQuestions, calibrationAnswers)
      : { question: null, answers: [] as GradingCalibrationAnswer[] }),
    [selectedFamilyId, calibrationQuestions, calibrationAnswers],
  );

  const activeRoleClusterId = selectedRoleClusterId
    ?? calib.answers.find((a) => a.id === calibAnswerId)?.target_role_cluster_id
    ?? null;

  const factorQuestions = useMemo(() => {
    if (!selectedFamilyId || !activeRoleClusterId) return [];
    return questionsByFactor(activeRoleClusterId, selectedFamilyId, factors, questions);
  }, [selectedFamilyId, activeRoleClusterId, factors, questions]);

  const totalScore = useMemo(
    () => Object.values(selectedAnswerIds).reduce((sum, answerId) => {
      const answer = answers.find((a) => a.id === answerId);
      return sum + (answer?.answer_score ?? 0);
    }, 0),
    [selectedAnswerIds, answers],
  );

  const matchedRole = useMemo(() => {
    if (!activeRoleClusterId) return undefined;
    return matchRoleByScore(roles, activeRoleClusterId, selectedSpecId, totalScore);
  }, [roles, activeRoleClusterId, selectedSpecId, totalScore]);

  const resetFullFlow = () => {
    setFullStep('family');
    setSelectedFamilyId(null);
    setSelectedSpecId(null);
    setSelectedRoleClusterId(null);
    setCalibAnswerId(null);
    setSelectedAnswerIds({});
  };

  const saveRole = async (roleId: number, assessmentPayload?: Parameters<typeof saveRecordAssessment>[1]) => {
    setSaving(true);
    setError(null);
    try {
      await updateRecord(record.id, { roleId, isRoleValidated: true });
      if (assessmentPayload) {
        await saveRecordAssessment(record.id, assessmentPayload);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const buildScoringPayload = () => Object.entries(selectedAnswerIds).flatMap(([questionId, answerId]) => {
    const answer = answers.find((a) => a.id === answerId);
    if (!answer) return [];
    return [{
      questionId: Number(questionId),
      answerId: answer.id,
      answerScore: answer.answer_score,
    }];
  });

  const handleQuickSave = () => {
    const roleId = Number(quickRoleId);
    if (!roleId) {
      setError('Выберите роль');
      return;
    }
    const familyId = Number(quickFamilyId);
    const roleClusterId = Number(quickRoleClusterId);
    if (!familyId || !roleClusterId) {
      setError('Выберите family и role cluster');
      return;
    }
    const specRaw = quickSpecId === '_none' || !quickSpecId ? null : Number(quickSpecId);
    saveRole(roleId, {
      mode: 'quick',
      familyId,
      roleClusterId,
      specializationId: specRaw,
      computedRoleId: roleId,
    });
  };

  const handleFullSave = () => {
    if (!matchedRole || !selectedFamilyId || !activeRoleClusterId) {
      setError('Не удалось определить роль по сумме баллов');
      return;
    }
    saveRole(matchedRole.id, {
      mode: 'full',
      familyId: selectedFamilyId,
      roleClusterId: activeRoleClusterId,
      specializationId: selectedSpecId,
      computedRoleId: matchedRole.id,
      totalScore,
      calibrationAnswerId: calibAnswerId,
      scoringAnswers: buildScoringPayload(),
    });
  };

  const goAfterFamily = () => {
    if (!selectedFamilyId) return;
    if (familySpecs.length) {
      setFullStep('specialization');
      return;
    }
    setFullStep('role_cluster');
  };

  const goAfterSpecialization = () => {
    setFullStep('role_cluster');
  };

  const goAfterRoleCluster = () => {
    if (calib.question && !calibAnswerId && !selectedRoleClusterId) {
      setError('Выберите role cluster или ответ калибровки');
      return;
    }
    if (!activeRoleClusterId) {
      setError('Выберите role cluster');
      return;
    }
    setError(null);
    setFullStep('scoring');
  };

  const renderQuickMode = () => {
    const hasSpecs = quickSpecOptions.length > 0;
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Последовательно выберите Family, Role cluster, Specialization и роль. Грейд будет взят из выбранной роли.
        </p>

        <AdminFieldTw label="Family" required>
          <select
            className={selectClass}
            value={quickFamilyId}
            onChange={(e) => {
              setQuickFamilyId(e.target.value);
              setQuickRoleClusterId('');
              setQuickSpecId('');
              setQuickRoleId('');
            }}
          >
            <option value="">— Выберите Family —</option>
            {families.map((family) => (
              <option key={family.id} value={family.id}>{family.name}</option>
            ))}
          </select>
        </AdminFieldTw>

        <AdminFieldTw label="Role cluster" required>
          <select
            className={selectClass}
            value={quickRoleClusterId}
            onChange={(e) => {
              setQuickRoleClusterId(e.target.value);
              setQuickSpecId('');
              setQuickRoleId('');
            }}
            disabled={!quickFamilyId}
          >
            <option value="">— Выберите Role cluster —</option>
            {quickRoleClusterOptions.map((rc) => (
              <option key={rc.id} value={rc.id}>{rc.name}</option>
            ))}
          </select>
        </AdminFieldTw>

        <AdminFieldTw
          label="Specialization"
          required={hasSpecs}
          hint={!hasSpecs && quickFamilyId ? 'Для выбранной Family specialization не требуется' : undefined}
        >
          <select
            className={selectClass}
            value={quickSpecId}
            onChange={(e) => {
              setQuickSpecId(e.target.value);
              setQuickRoleId('');
            }}
            disabled={!quickRoleClusterId || !hasSpecs}
          >
            <option value="">
              {hasSpecs ? '— Выберите Specialization —' : '— Не требуется —'}
            </option>
            {hasSpecs && <option value="_none">Без specialization</option>}
            {quickSpecOptions.map((spec) => (
              <option key={spec.id} value={spec.id}>{spec.name}</option>
            ))}
          </select>
        </AdminFieldTw>

        <AdminFieldTw label="Роль" required>
          <select
            className={selectClass}
            value={quickRoleId}
            onChange={(e) => setQuickRoleId(e.target.value)}
            disabled={!quickRoleClusterId || (hasSpecs && !quickSpecId)}
          >
            <option value="">— Выберите роль —</option>
            {quickRoleOptions.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name} · G{role.grade_level}
                {role.min_score != null && role.max_score != null
                  ? ` (${formatGradingScore(role.min_score)}–${formatGradingScore(role.max_score)} б.)`
                  : ''}
              </option>
            ))}
          </select>
        </AdminFieldTw>

        {quickSelectedRole && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
            <p className="font-medium text-slate-900">
              {buildRoleLabel(quickSelectedRole, specializations, roleClusters, families)}
            </p>
            <p className="mt-1 text-slate-600">Уровень грейда: G{quickSelectedRole.grade_level}</p>
          </div>
        )}
      </div>
    );
  };

  const renderScoringQuestion = (
    factor: GradingDiffFactor,
    question: GradingQuestion,
    questionIndexInFactor: number,
    questionsInFactor: number,
  ) => {
    const qAnswers = visibleAnswersForQuestion(answers, question.id);
    return (
      <div key={question.id} className="dc-grading-preview-question">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
          {factor.name}
          {questionsInFactor > 1 ? ` · ${questionIndexInFactor}/${questionsInFactor}` : ''}
          {question.name ? ` · ${question.name}` : ''}
        </p>
        <p className="font-medium text-slate-900">{question.question_text}</p>
        <div className="mt-3 space-y-2">
          {qAnswers.map((a) => (
            <label
              key={a.id}
              className={`dc-grading-preview-option ${selectedAnswerIds[question.id] === a.id ? 'dc-grading-preview-option-selected' : ''}`}
            >
              <input
                type="radio"
                name={`q-${question.id}`}
                checked={selectedAnswerIds[question.id] === a.id}
                onChange={() => setSelectedAnswerIds((prev) => ({ ...prev, [question.id]: a.id }))}
              />
              <span className="flex-1">{a.answer_text}</span>
              <span className="dc-grading-answer-score">{formatGradingScore(a.answer_score)} б.</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderFullMode = () => {
    if (fullStep === 'family') {
      return (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Выберите family для анкеты грейдирования.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {families.map((family) => (
              <button
                key={family.id}
                type="button"
                className={`dc-grading-family-card text-left ${selectedFamilyId === family.id ? 'dc-grading-family-card-active' : ''}`}
                onClick={() => {
                  setSelectedFamilyId(family.id);
                  setSelectedSpecId(null);
                  setSelectedRoleClusterId(null);
                  setCalibAnswerId(null);
                  setSelectedAnswerIds({});
                }}
              >
                <span className="font-medium text-slate-900">{family.name}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (fullStep === 'specialization') {
      return (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Выберите specialization (если применимо).</p>
          <div className="space-y-2">
            {familySpecs.map((spec) => (
              <label key={spec.id} className="dc-grading-preview-option">
                <input
                  type="radio"
                  name="spec"
                  checked={selectedSpecId === spec.id}
                  onChange={() => setSelectedSpecId(spec.id)}
                />
                <span>{spec.name}</span>
              </label>
            ))}
            <label className="dc-grading-preview-option">
              <input
                type="radio"
                name="spec"
                checked={selectedSpecId === null}
                onChange={() => setSelectedSpecId(null)}
              />
              <span>Без specialization</span>
            </label>
          </div>
        </div>
      );
    }

    if (fullStep === 'role_cluster') {
      return (
        <div className="space-y-4">
          {calib.question ? (
            <>
              <p className="text-sm text-slate-600">{calib.question.question_text}</p>
              <div className="space-y-2">
                {calib.answers.map((a) => (
                  <label key={a.id} className="dc-grading-preview-option">
                    <input
                      type="radio"
                      name="calib"
                      checked={calibAnswerId === a.id}
                      onChange={() => {
                        setCalibAnswerId(a.id);
                        setSelectedRoleClusterId(null);
                      }}
                    />
                    <span>{a.answer_text}</span>
                  </label>
                ))}
              </div>
            </>
          ) : null}
          {!calib.question && (
            <>
              <p className="text-sm text-slate-600">Выберите role cluster.</p>
              <div className="space-y-2">
                {familyRoleClusters.map((rc) => (
                  <label key={rc.id} className="dc-grading-preview-option">
                    <input
                      type="radio"
                      name="role_cluster"
                      checked={selectedRoleClusterId === rc.id}
                      onChange={() => {
                        setSelectedRoleClusterId(rc.id);
                        setCalibAnswerId(null);
                      }}
                    />
                    <span>{rc.name}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      );
    }

    if (fullStep === 'scoring') {
      const allAnswered = factorQuestions.length > 0
        && factorQuestions.every(({ question }) => selectedAnswerIds[question.id] != null);
      return (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Ответьте на каждый вопрос. Сумма баллов по всем ответам определит роль.
          </p>
          {factorQuestions.length === 0 ? (
            <p className="text-sm text-amber-700">Для выбранного role cluster нет вопросов по факторам.</p>
          ) : (
            factorQuestions.map(({ factor, question, questionIndexInFactor, questionsInFactor }) => (
              renderScoringQuestion(factor, question, questionIndexInFactor, questionsInFactor)
            ))
          )}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
            Сумма баллов: <strong>{formatGradingScore(totalScore)}</strong>
            {matchedRole && (
              <span className="ml-2 text-slate-600">
                → {buildRoleLabel(matchedRole, specializations, roleClusters, families)}
              </span>
            )}
          </div>
          {!allAnswered && factorQuestions.length > 0 && (
            <p className="text-xs text-slate-500">Ответьте на все вопросы, чтобы перейти к итогу.</p>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {matchedRole ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4">
            <p className="text-sm text-emerald-900">Рекомендуемая роль:</p>
            <p className="mt-1 text-lg font-semibold text-emerald-950">
              {buildRoleLabel(matchedRole, specializations, roleClusters, families)}
            </p>
            <p className="mt-2 text-sm text-emerald-800">Сумма баллов: {formatGradingScore(totalScore)}</p>
          </div>
        ) : (
          <p className="text-sm text-red-700">
            Ни одна роль не соответствует сумме {formatGradingScore(totalScore)} баллов для выбранного role cluster.
          </p>
        )}
      </div>
    );
  };

  const renderFooter = () => {
    if (mode === 'quick') {
      return (
        <>
          <button type="button" className={btnSecondary} onClick={onClose} disabled={saving}>Отмена</button>
          <button type="button" className={btnPrimary} onClick={handleQuickSave} disabled={saving || loading}>
            {saving ? 'Сохранение…' : 'Назначить роль'}
          </button>
        </>
      );
    }

    if (fullStep === 'family') {
      return (
        <>
          <button type="button" className={btnSecondary} onClick={onClose} disabled={saving}>Отмена</button>
          <button type="button" className={btnPrimary} onClick={goAfterFamily} disabled={!selectedFamilyId}>
            Далее
          </button>
        </>
      );
    }

    if (fullStep === 'specialization') {
      return (
        <>
          <button type="button" className={btnSecondary} onClick={() => setFullStep('family')}>Назад</button>
          <button type="button" className={btnPrimary} onClick={goAfterSpecialization}>Далее</button>
        </>
      );
    }

    if (fullStep === 'role_cluster') {
      return (
        <>
          <button
            type="button"
            className={btnSecondary}
            onClick={() => setFullStep(familySpecs.length ? 'specialization' : 'family')}
          >
            Назад
          </button>
          <button type="button" className={btnPrimary} onClick={goAfterRoleCluster}>К вопросам</button>
        </>
      );
    }

    if (fullStep === 'scoring') {
      const allAnswered = factorQuestions.length > 0
        && factorQuestions.every(({ question }) => selectedAnswerIds[question.id] != null);
      return (
        <>
          <button type="button" className={btnSecondary} onClick={() => setFullStep('role_cluster')}>Назад</button>
          <button
            type="button"
            className={btnPrimary}
            onClick={() => setFullStep('result')}
            disabled={!allAnswered}
          >
            К итогу
          </button>
        </>
      );
    }

    return (
      <>
        <button type="button" className={btnSecondary} onClick={() => setFullStep('scoring')} disabled={saving}>
          Назад
        </button>
        <button
          type="button"
          className={btnPrimary}
          onClick={handleFullSave}
          disabled={saving || !matchedRole}
        >
          {saving ? 'Сохранение…' : 'Назначить роль'}
        </button>
      </>
    );
  };

  const employeeLabel = [record.last_name, record.first_name, record.middle_name]
    .filter(Boolean)
    .join(' ')
    || record.company_role_name;

  return (
    <AdminModalTw
      title="Назначить грейд"
      onClose={onClose}
      wide
      footer={renderFooter()}
    >
      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
        <p className="font-medium text-slate-900">{employeeLabel}</p>
        <p className="mt-1 text-slate-600">
          {record.company_name} · {record.company_role_name}
          {record.role_name ? ` · текущая роль: ${record.role_name}` : ''}
        </p>
      </div>

      <div className="mb-4 flex gap-2">
        <button
          type="button"
          className={`rounded-lg px-3 py-1.5 text-sm font-medium ${mode === 'quick' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}
          onClick={() => { setMode('quick'); setError(null); }}
        >
          Быстрый
        </button>
        <button
          type="button"
          className={`rounded-lg px-3 py-1.5 text-sm font-medium ${mode === 'full' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}
          onClick={() => { setMode('full'); setError(null); resetFullFlow(); }}
        >
          Обычный
        </button>
      </div>

      {loading && <p className="text-sm text-slate-500">Загрузка дерева грейдинга…</p>}
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {!loading && mode === 'quick' && renderQuickMode()}
      {!loading && mode === 'full' && renderFullMode()}
    </AdminModalTw>
  );
}
